import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  isDevMode,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { TwoFactorComponent as BaseTwoFactorComponent } from "@bitwarden/angular/components/two-factor.component";
import { ModalService } from "@bitwarden/angular/services/modal.service";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { AppIdService } from "@bitwarden/common/abstractions/appId.service";
import { AuthService } from "@bitwarden/common/abstractions/auth.service";
import { EnvironmentService } from "@bitwarden/common/abstractions/environment.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { LoginService } from "@bitwarden/common/abstractions/login.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { TwoFactorService } from "@bitwarden/common/abstractions/twoFactor.service";
import { TwoFactorProviderType } from "@bitwarden/common/enums/twoFactorProviderType";

import { RouterService } from "@bitwarden/web-vault/app/core";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";

import { BiometricSetupHandoffService } from "../login/biometric-setup-handoff.service";

import { TwoFactorOptionsComponent } from "./two-factor-options.component";

@Component({
  selector: "app-two-factor",
  templateUrl: "two-factor.component.html",
})
// eslint-disable-next-line rxjs-angular/prefer-takeuntil
export class TwoFactorComponent extends BaseTwoFactorComponent implements AfterViewInit {
  @ViewChild("twoFactorOptions", { read: ViewContainerRef, static: true })
  twoFactorOptionsModal: ViewContainerRef;

  @ViewChildren("otpInput") private otpInputRefs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    authService: AuthService,
    router: Router,
    i18nService: I18nService,
    apiService: ApiService,
    platformUtilsService: PlatformUtilsService,
    stateService: StateService,
    environmentService: EnvironmentService,
    private modalService: ModalService,
    route: ActivatedRoute,
    logService: LogService,
    twoFactorService: TwoFactorService,
    appIdService: AppIdService,
    private routerService: RouterService,
    loginService: LoginService,
  ) {
    super(
      authService,
      router,
      i18nService,
      apiService,
      platformUtilsService,
      window,
      environmentService,
      stateService,
      route,
      logService,
      twoFactorService,
      appIdService,
      loginService,
    );
    this.onSuccessfulLoginNavigate = this.goAfterLogIn;
  }

  otpDigits: string[] = ["", "", "", "", "", ""];
  private otpSubmitting = false;
  private biometricSetupHandoff = inject(BiometricSetupHandoffService);

  /** Continue's onClick: one stable reference (a `.bind()` in the template made a new function, and
   * so a new gloss-button options object, on every change detection). */
  protected readonly onContinueClick = () => this.submitOtp();

  trackByIndex(index: number): number {
    return index;
  }

  private get otpEls(): HTMLInputElement[] {
    return this.otpInputRefs?.toArray().map((r) => r.nativeElement) ?? [];
  }

  ngAfterViewInit() {
    if (this.otpInputRefs.length > 0) {
      // Inputs are already in the DOM (dev preview / synchronous init path)
      this.otpInputRefs.first.nativeElement.focus();
    } else {
      // eslint-disable-next-line rxjs-angular/prefer-takeuntil
      const sub = this.otpInputRefs.changes.subscribe(
        (refs: QueryList<ElementRef<HTMLInputElement>>) => {
          if (refs.length > 0) {
            refs.first.nativeElement.focus();
            sub.unsubscribe();
          }
        },
      );
    }
  }

  onOtpInput(event: Event, index: number) {
    const inputs = this.otpEls;
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, "").slice(-1);
    input.value = value;
    this.otpDigits[index] = value;
    this.token = this.otpDigits.join("");
    if (value && index < 5) {
      inputs[index + 1].focus();
    } else if (value && index === 5) {
      this.submitOtp();
    }
  }

  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const inputs = this.otpEls;
    if (event.key === "Backspace") {
      const input = event.target as HTMLInputElement;
      if (!this.otpDigits[index] && index > 0) {
        this.otpDigits[index - 1] = "";
        inputs[index - 1].value = "";
        this.token = this.otpDigits.join("");
        inputs[index - 1].focus();
        event.preventDefault();
      } else {
        this.otpDigits[index] = "";
        input.value = "";
        this.token = this.otpDigits.join("");
        event.preventDefault();
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      inputs[index - 1].focus();
    } else if (event.key === "ArrowRight" && index < 5) {
      event.preventDefault();
      inputs[index + 1].focus();
    } else if (event.key === "Enter" && !event.isComposing) {
      // The email block has six text inputs and no submit button (gloss-button is always
      // type="button"), so the browser never submits the form on Enter. Submit through the same
      // guarded path as Continue; a held key's repeats don't re-submit, and an Enter that only
      // commits an IME composition is left alone.
      event.preventDefault();
      if (!event.repeat) {
        this.submitOtp();
      }
    }
  }

  onOtpFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    setTimeout(() => {
      if (document.activeElement === input) {
        input.select();
      }
    }, 0);
  }

  onOtpPaste(event: ClipboardEvent) {
    const inputs = this.otpEls;
    event.preventDefault();
    const text = (event.clipboardData?.getData("text") ?? "").replace(/\D/g, "").slice(0, 6);
    this.otpDigits = ["", "", "", "", "", ""];
    inputs.forEach((i) => (i.value = ""));
    text.split("").forEach((char, i) => {
      this.otpDigits[i] = char;
      if (inputs[i]) {
        inputs[i].value = char;
      }
    });
    this.token = this.otpDigits.join("");
    inputs[Math.min(text.length, 5)]?.focus();
    if (text.length === 6) {
      this.submitOtp();
    }
  }

  /**
   * Single entry point for the email OTP submit (auto-submit on the 6th digit, paste, Enter in a
   * code box, and the Continue button), so a Continue click while an auto-submit is still in
   * flight (form.loading only flips once doSubmit() sets formPromise) can't send the code twice.
   */
  submitOtp() {
    if (this.otpSubmitting) {
      return;
    }
    // The Continue button is type="button", so the inputs' `required` never runs; don't spend a
    // 2FA attempt on a partial code (submit() itself only rejects an empty one).
    const codeLength = this.token?.length ?? 0;
    if (codeLength > 0 && codeLength < this.otpDigits.length) {
      this.platformUtilsService.showToast(
        "error",
        this.i18nService.t("errorOccurred"),
        this.i18nService.t("verificationCodeRequired"),
      );
      return;
    }
    this.otpSubmitting = true;
    this.submit()
      .catch((e) => this.logService.error(e))
      .finally(() => (this.otpSubmitting = false));
  }

  override async ngOnInit() {
    if (isDevMode() && this.route.snapshot.queryParams["devPreview"] === "email") {
      this.selectedProviderType = TwoFactorProviderType.Email;
      this.twoFactorEmail = "preview@example.com";
      return;
    }
    await super.ngOnInit();
  }

  async anotherMethod() {
    const [modal] = await this.modalService.openViewRef(
      TwoFactorOptionsComponent,
      this.twoFactorOptionsModal,
      (comp) => {
        // eslint-disable-next-line rxjs-angular/prefer-takeuntil, rxjs/no-async-subscribe
        comp.onProviderSelected.subscribe(async (provider: TwoFactorProviderType) => {
          modal.close();
          this.otpDigits = ["", "", "", "", "", ""];
          this.otpSubmitting = false;
          this.token = "";
          this.selectedProviderType = provider;
          await this.init();
        });
        // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        comp.onRecoverSelected.subscribe(() => {
          modal.close();
        });
      },
    );
  }

  async goAfterLogIn() {
    this.loginService.clearValues();
    // Consumed unconditionally so the intent can't outlive this login. Only the plain
    // success route gets the setup screen — a password reset or lock takes precedence,
    // mirroring LoginComponent, whose setup route only runs on a plain successful login.
    const setupBiometrics = this.biometricSetupHandoff.consumePendingSetup();
    if (setupBiometrics && this.successRoute === GlossRoutes.Views) {
      this.router.navigate(["/two-factor-setup"], { queryParams: { setupBiometrics: "true" } });
      return;
    }
    const previousUrl = this.routerService.getPreviousUrl();
    if (previousUrl) {
      this.router.navigateByUrl(previousUrl);
    } else {
      this.router.navigate([this.successRoute], {
        queryParams: {
          identifier: this.identifier,
        },
      });
    }
  }
}
